import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Card, CircularProgress, LinearProgress,
  Chip, Stepper, Step, StepLabel, StepButton, Accordion, AccordionSummary,
  AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Snackbar, Stack, Drawer, IconButton, useMediaQuery, useTheme,
  alpha, Paper,
} from "@mui/material";
import {
  ExpandMore, CheckCircle, PlayCircle, Lock, EmojiEvents,
  AccessTime, MenuBook, Description, VideoLibrary, Link as LinkIcon,
  Menu, Close, ArrowBack, NavigateBefore, NavigateNext,
  RadioButtonUnchecked, TimelapseOutlined,
} from "@mui/icons-material";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API_BASE_URL = "https://backend-global-1vq9.onrender.com/api/lessons";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
  gold: "#F59E0B",
  goldSoft: "#FEF3C7",
};

// ── Section status icon ───────────────────────────────────────────────────────
function SectionIcon({ status, isActive, isLocked }) {
  if (status === "completed") return <CheckCircle sx={{ color: C.secondary, fontSize: 22 }} />;
  if (isLocked) return <Lock sx={{ color: C.textMuted, fontSize: 22 }} />;
  if (isActive) return <PlayCircle sx={{ color: C.primary, fontSize: 22 }} />;
  return <RadioButtonUnchecked sx={{ color: C.textMuted, fontSize: 22 }} />;
}

export default function LessonDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [certificateDialog, setCertificateDialog] = useState(false);
  const [notes, setNotes] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const learnerId = user?.id || user?._id;

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => { loadLessonAndProgress(); }, [id]);

  const loadLessonAndProgress = async () => {
    try {
      setLoading(true);
      const lessonRes = await fetch(`${API_BASE_URL}/${id}`);
      const lessonData = await lessonRes.json();
      if (!lessonRes.ok) throw new Error(lessonData.message || "Failed to load lesson");
      setLesson(lessonData.data);

      if (!learnerId) { setLoading(false); showSnackbar("Please log in to track progress", "warning"); return; }

      try {
        const progressRes = await fetch(`${API_BASE_URL}/progress/${learnerId}/${id}`);
        if (progressRes.ok) {
          const progressData = await progressRes.json();
          setProgress(progressData.data);
          const notesMap = {};
          progressData.data.sectionsProgress?.forEach((sp) => { if (sp.notes) notesMap[sp.sectionId] = sp.notes; });
          setNotes(notesMap);
          const firstIncomplete = progressData.data.sectionsProgress?.findIndex((sp) => sp.status !== "completed");
          if (firstIncomplete >= 0) setActiveSection(firstIncomplete);
        } else { await startLesson(); }
      } catch { await startLesson(); }
    } catch (err) { showSnackbar(err.message || "Failed to load lesson", "error"); }
    finally { setLoading(false); }
  };

  const startLesson = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/progress/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ learnerId, lessonId: id }),
      });
      if (res.ok) { const data = await res.json(); setProgress(data.data); showSnackbar("Lesson started! Good luck 🚀", "success"); }
      else { const e = await res.json(); throw new Error(e.message); }
    } catch (err) { showSnackbar(err.message || "Failed to start lesson", "error"); }
  };

  const updateSectionProgress = async (sectionId, status, timeSpent = 0) => {
    if (!learnerId || !progress) { showSnackbar("Please log in to save progress", "warning"); return; }
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_URL}/progress/section`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ learnerId, lessonId: id, sectionId, status, timeSpent, notes: notes[sectionId] || "" }),
      });
      if (res.ok) {
        const data = await res.json();
        setProgress(data.data);
        if (status === "completed") showSnackbar("Section completed! 🎉", "success");
        if (data.data.completionPercentage === 100 && !data.data.certificateIssued && lesson.certificate?.enabled) {
          setCertificateDialog(true);
        }
      } else { const e = await res.json(); throw new Error(e.message); }
    } catch (err) { showSnackbar(err.message || "Failed to update progress", "error"); }
    finally { setSaving(false); }
  };

  const completeSection = async (sectionIndex) => {
    const section = lesson.sections[sectionIndex];
    await updateSectionProgress(section._id, "completed", section.duration);
    if (sectionIndex < lesson.sections.length - 1) {
      setActiveSection(sectionIndex + 1);
      if (isMobile) setSidebarOpen(false);
    }
  };

  const saveNotes = async (sectionId) => {
    if (!learnerId || !progress) { showSnackbar("Please log in to save notes", "warning"); return; }
    const sp = progress.sectionsProgress?.find((s) => s.sectionId === sectionId);
    if (sp) { await updateSectionProgress(sectionId, sp.status, sp.timeSpent); showSnackbar("Notes saved 📝", "success"); }
  };

  const requestCertificate = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/certificate/issue`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ learnerId, lessonId: id }),
      });
      const data = await res.json();
      if (res.ok) {
        showSnackbar("Certificate issued! 🏆", "success");
        setCertificateDialog(false);
        setTimeout(() => navigate(`/certificate/${id}`), 1000);
      } else throw new Error(data.message);
    } catch (err) { showSnackbar(err.message || "Failed to request certificate", "error"); }
  };

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const getSectionStatus = (sectionId) => {
    if (!progress?.sectionsProgress) return "not_started";
    return progress.sectionsProgress.find((s) => s.sectionId === sectionId)?.status || "not_started";
  };

  // ── Loading / error states ────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={52} sx={{ color: C.primary }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (!lesson) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
          <MenuBook sx={{ fontSize: 56, color: C.textMuted, opacity: 0.5 }} />
          <Typography fontWeight={600} color="text.secondary">Lesson not found</Typography>
          <Button onClick={() => navigate("/curriculum")} variant="outlined"
            sx={{ borderRadius: 2.5, textTransform: "none", borderColor: C.primary, color: C.primary,
              "&:hover": { bgcolor: C.primarySoft, borderColor: C.primaryDark } }}>
            ← Back to Lessons
          </Button>
        </Box>
        <Footer />
      </Box>
    );
  }

  const currentSection = lesson.sections[activeSection];
  const currentSectionStatus = currentSection ? getSectionStatus(currentSection._id) : "not_started";
  const lessonCompleted = (progress?.completionPercentage || 0) >= 100;

  // ── Sidebar content (shared by desktop + mobile drawer) ──────────────────────
  const SidebarContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Box sx={{ width: 4, height: 22, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, borderRadius: 1 }} />
        <Typography fontWeight={700} fontSize={14} color={C.textPrimary}>
          Course Content
        </Typography>
      </Stack>

      {progress && (
        <Box mb={2.5}>
          <Stack direction="row" justifyContent="space-between" mb={0.75}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 700, color: C.textMuted,
              textTransform: "uppercase", letterSpacing: 0.5,
            }}>
              Overall Progress
            </Typography>
            <Typography sx={{
              fontSize: 12, fontWeight: 700,
              color: lessonCompleted ? C.secondary : C.primary,
            }}>
              {progress.completionPercentage}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate" value={progress.completionPercentage}
            sx={{
              height: 8, borderRadius: 4, bgcolor: C.borderSoft,
              "& .MuiLinearProgress-bar": {
                background: lessonCompleted
                  ? `linear-gradient(90deg, ${C.secondary}, ${C.secondaryLight})`
                  : `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
                borderRadius: 4,
              },
            }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Stepper
          activeStep={activeSection}
          orientation="vertical"
          sx={{ "& .MuiStepConnector-line": { borderColor: C.borderSoft, ml: 0.3 } }}
        >
          {lesson.sections?.map((section, index) => {
            const status = getSectionStatus(section._id);
            const isLocked = index > 0 && getSectionStatus(lesson.sections[index - 1]._id) !== "completed";
            const isActive = activeSection === index;
            const isComplete = status === "completed";

            return (
              <Step key={section._id} completed={isComplete}>
                <StepButton
                  onClick={() => {
                    if (!isLocked) {
                      setActiveSection(index);
                      if (isMobile) setSidebarOpen(false);
                    }
                  }}
                  disabled={isLocked}
                  sx={{
                    py: 0.75, borderRadius: 2,
                    bgcolor: isActive ? C.primarySofter : "transparent",
                    "&:hover": { bgcolor: isLocked ? "transparent" : C.primarySofter },
                  }}
                >
                  <StepLabel StepIconComponent={() => (
                    <SectionIcon status={status} isActive={isActive} isLocked={isLocked} />
                  )}>
                    <Box>
                      <Typography sx={{
                        fontSize: 12.5,
                        fontWeight: isActive ? 700 : 600,
                        color: isComplete ? C.secondary : isActive ? C.primary : C.textPrimary,
                        lineHeight: 1.35,
                        textDecoration: isComplete ? "line-through" : "none",
                        textDecorationColor: alpha(C.textMuted, 0.4),
                      }}>
                        {section.title}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" mt={0.3}>
                        <AccessTime sx={{ fontSize: 10.5, color: C.textMuted }} />
                        <Typography sx={{ fontSize: 10.5, color: C.textMuted, fontWeight: 600 }}>
                          {section.duration} mins
                        </Typography>
                      </Stack>
                    </Box>
                  </StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </Box>
  );

  // ── Accordion helper ──────────────────────────────────────────────────────────
  const SectionAccordion = ({ icon, title, defaultExpanded = false, children }) => (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        borderRadius: "12px !important",
        border: `1px solid ${C.border}`,
        boxShadow: "none", mb: 1.5,
        "&:before": { display: "none" },
        "&.Mui-expanded": { m: "0 0 12px 0" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: C.primary }} />}
        sx={{
          borderRadius: 2, minHeight: 48,
          "&.Mui-expanded": { minHeight: 48 },
          "& .MuiAccordionSummary-content": { my: 1 },
          "&:hover": { bgcolor: C.primarySofter },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ color: C.primary, display: "flex" }}>{icon}</Box>
          <Typography fontWeight={700} fontSize={13.5} color={C.textPrimary}>{title}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2, px: 2.5 }}>{children}</AccordionDetails>
    </Accordion>
  );

  // Status config for the header chip
  const headerStatus = currentSectionStatus === "completed"
    ? { label: "Completed", icon: <CheckCircle sx={{ fontSize: "13px !important", color: "#fff !important" }} />, bg: alpha(C.secondaryLight, 0.5) }
    : currentSectionStatus === "in_progress"
      ? { label: "In Progress", icon: <TimelapseOutlined sx={{ fontSize: "13px !important", color: "#fff !important" }} />, bg: "rgba(255,255,255,0.22)" }
      : { label: "Not Started", icon: <RadioButtonUnchecked sx={{ fontSize: "13px !important", color: "#fff !important" }} />, bg: "rgba(255,255,255,0.18)" };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      {/* ── Hero header ── */}
      <Box sx={{ px: { xs: 1.5, sm: 3, md: 6, lg: 10 }, pt: { xs: 2.5, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: lessonCompleted
              ? `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryDark} 100%)`
              : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2, md: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${lessonCompleted ? C.secondary : C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: lessonCompleted
              ? `radial-gradient(circle, ${C.secondaryLight}50 0%, transparent 70%)`
              : `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}
            sx={{ position: "relative", zIndex: 1 }}>
            <Box flex={1} minWidth={0}>
              {/* Mobile: menu + back */}
              {isMobile && (
                <Stack direction="row" spacing={1} mb={1.5}>
                  <IconButton size="small" onClick={() => setSidebarOpen(true)}
                    sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.18)", borderRadius: 1.5,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.28)" } }}>
                    <Menu fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => navigate("/curriculum")}
                    sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.18)", borderRadius: 1.5,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.28)" } }}>
                    <ArrowBack fontSize="small" />
                  </IconButton>
                </Stack>
              )}

              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 0.75,
              }}>
                {lessonCompleted ? "Lesson Complete" : "Lesson"}
              </Typography>

              <Typography sx={{
                fontWeight: 800, color: "#fff",
                fontSize: { xs: 20, sm: 26, md: 32 },
                lineHeight: 1.2, mb: 1.25, letterSpacing: -0.5,
              }}>
                {lesson.title}
              </Typography>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={1.5}>
                {lesson.category && (
                  <Chip label={lesson.category} size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.22)", color: "#fff",
                      fontSize: 11, fontWeight: 700, height: 24,
                      textTransform: "capitalize", backdropFilter: "blur(4px)",
                    }} />
                )}
                {lesson.difficulty && (
                  <Chip label={lesson.difficulty} size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.22)", color: "#fff",
                      fontSize: 11, fontWeight: 700, height: 24,
                      textTransform: "capitalize",
                    }} />
                )}
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <MenuBook sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} />
                  <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                    {lesson.sections?.length || 0} {lesson.sections?.length === 1 ? "section" : "sections"}
                  </Typography>
                </Stack>
                {lesson.totalDuration > 0 && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <AccessTime sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} />
                    <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                      {lesson.totalDuration} minutes
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {/* Inline overall progress when started */}
              {progress && (progress.completionPercentage || 0) > 0 && (
                <Box sx={{ mt: 2, maxWidth: 420 }}>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>
                      Your progress
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#fff" }}>
                      {progress.completionPercentage}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate" value={progress.completionPercentage}
                    sx={{
                      height: 7, borderRadius: 4,
                      bgcolor: "rgba(255,255,255,0.18)",
                      "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(90deg, #fff, rgba(255,255,255,0.8))",
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            {progress?.certificateIssued && (
              <Chip
                icon={<EmojiEvents sx={{ color: "#000 !important" }} />}
                label="View Certificate"
                onClick={() => navigate(`/certificate/${id}`)}
                sx={{
                  bgcolor: "#FFD700", color: "#000",
                  fontWeight: 700, px: 1, py: 2.5,
                  cursor: "pointer",
                  alignSelf: "flex-start",
                  "&:hover": { bgcolor: "#FFC700" },
                }}
              />
            )}
          </Stack>
        </Paper>
      </Box>

      {/* Login alert */}
      {!learnerId && (
        <Box sx={{ px: { xs: 2, sm: 3, md: 6, lg: 10 }, mb: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
            Please log in to track your progress and earn certificates
          </Alert>
        </Box>
      )}

      {/* ── Main content ── */}
      <Box sx={{ flex: 1, px: { xs: 1.5, sm: 3, md: 6, lg: 10 }, pb: { xs: 4, md: 5 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

          {/* Desktop sidebar */}
          {!isMobile && (
            <Paper
              elevation={0}
              sx={{
                width: 300, flexShrink: 0, p: 2.5,
                borderRadius: 3, border: `1px solid ${C.border}`,
                bgcolor: C.paper,
                position: "sticky", top: 20, maxHeight: "80vh",
                overflow: "hidden",
                display: "flex", flexDirection: "column",
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              }}
            >
              <SidebarContent />
            </Paper>
          )}

          {/* Mobile sidebar drawer */}
          <Drawer
            anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}
            PaperProps={{ sx: { width: 300, p: 2.5, display: "flex", flexDirection: "column" } }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography fontWeight={700} fontSize={14} color={C.textPrimary}>Course Content</Typography>
              <IconButton size="small" onClick={() => setSidebarOpen(false)}><Close fontSize="small" /></IconButton>
            </Stack>
            <SidebarContent />
          </Drawer>

          {/* ── Current section ── */}
          <Box flex={1} minWidth={0}>
            {currentSection && (
              <Card elevation={0} sx={{
                borderRadius: 3, border: `1px solid ${C.border}`,
                mb: 2, overflow: "hidden",
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              }}>
                {/* Section header */}
                <Box sx={{
                  background: currentSectionStatus === "completed"
                    ? `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryDark} 100%)`
                    : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                  px: { xs: 2.5, sm: 3 }, py: 2.5,
                }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{
                        fontWeight: 800, color: "#fff",
                        fontSize: { xs: 16, sm: 18 }, mb: 0.75, lineHeight: 1.3,
                      }}>
                        {currentSection.title}
                      </Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={0.75}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <AccessTime sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }} />
                          <Typography fontSize={11.5} sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                            {currentSection.duration} mins
                          </Typography>
                        </Stack>
                        <Chip
                          icon={headerStatus.icon}
                          label={headerStatus.label}
                          size="small"
                          sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: headerStatus.bg, color: "#fff",
                            "& .MuiChip-label": { px: 0.75 },
                            "& .MuiChip-icon": { color: "#fff" },
                          }}
                        />
                      </Stack>
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.75)", fontWeight: 600, flexShrink: 0 }}>
                      {activeSection + 1} / {lesson.sections.length}
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  {currentSection.description && (
                    <Typography sx={{
                      fontSize: 14, color: C.textSecondary,
                      lineHeight: 1.7, mb: 2.5,
                    }}>
                      {currentSection.description}
                    </Typography>
                  )}

                  {/* Materials */}
                  {currentSection.materials?.length > 0 && (
                    <SectionAccordion
                      icon={<MenuBook sx={{ fontSize: 18 }} />}
                      title={`Open material to start learning (${currentSection.materials.length})`}
                      defaultExpanded
                    >
                      <Stack spacing={1}>
                        {currentSection.materials.map((m, i) => (
                          <Button
                            key={i} variant="outlined" href={m.url} target="_blank"
                            startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              justifyContent: "flex-start", textTransform: "none",
                              fontSize: 13, fontWeight: 600,
                              borderRadius: 2, borderColor: C.border, color: C.textPrimary,
                              py: 1, px: 1.5,
                              "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
                            }}
                          >
                            {m.title} ({m.type})
                          </Button>
                        ))}
                      </Stack>
                    </SectionAccordion>
                  )}

                  {/* Action buttons */}
                  {learnerId && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={3} flexWrap="wrap" gap={1}>
                      {currentSectionStatus !== "completed" && (
                        <Button
                          variant="contained" size="large"
                          onClick={() => completeSection(activeSection)}
                          disabled={saving}
                          startIcon={<CheckCircle sx={{ fontSize: 17 }} />}
                          sx={{
                            borderRadius: 2.5, textTransform: "none",
                            fontWeight: 700, fontSize: 14, px: 3,
                            background: `linear-gradient(135deg, ${C.secondary}, ${C.secondaryDark})`,
                            boxShadow: "none",
                            "&:hover": { boxShadow: `0 4px 14px ${C.secondary}45` },
                            "&.Mui-disabled": { opacity: 0.6 },
                          }}
                        >
                          {saving ? "Saving…" : "Mark as Complete"}
                        </Button>
                      )}

                      <Stack direction="row" spacing={1}>
                        {activeSection > 0 && (
                          <Button
                            variant="outlined" startIcon={<NavigateBefore />}
                            onClick={() => setActiveSection((a) => a - 1)}
                            sx={{
                              borderRadius: 2.5, textTransform: "none",
                              fontWeight: 700, fontSize: 13,
                              borderColor: C.border, color: C.textSecondary,
                              "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
                            }}
                          >
                            Prev
                          </Button>
                        )}
                        {activeSection < lesson.sections.length - 1 && (
                          <Button
                            variant="outlined" endIcon={<NavigateNext />}
                            onClick={() => setActiveSection((a) => a + 1)}
                            disabled={
                              getSectionStatus(lesson.sections[activeSection + 1]._id) === "not_started" &&
                              currentSectionStatus !== "completed"
                            }
                            sx={{
                              borderRadius: 2.5, textTransform: "none",
                              fontWeight: 700, fontSize: 13,
                              borderColor: C.border, color: C.textSecondary,
                              "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
                              "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted },
                            }}
                          >
                            Next
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </Card>
            )}

            {!isMobile && (
              <Button
                variant="outlined" startIcon={<ArrowBack sx={{ fontSize: 15 }} />}
                onClick={() => navigate("/curriculum")}
                sx={{
                  borderRadius: 2.5, textTransform: "none",
                  fontWeight: 700, fontSize: 13,
                  borderColor: C.border, color: C.textSecondary,
                  "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
                }}
              >
                Back to Lessons
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* ── Certificate dialog ── */}
      <Dialog
        open={certificateDialog} onClose={() => setCertificateDialog(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <EmojiEvents sx={{ fontSize: 60, color: C.gold, mb: 1 }} />
          <Typography variant="h5" fontWeight={800} color={C.textPrimary}>
            Congratulations! 🎉
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography textAlign="center" fontSize={14} color={C.textSecondary} mb={1}>
            You've completed all sections! Would you like to receive your certificate?
          </Typography>
          <Typography textAlign="center" fontSize={12} color={C.textMuted}>
            Your achievement will be documented with a downloadable certificate.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1, flexDirection: { xs: "column", sm: "row" }, px: 3 }}>
          <Button
            fullWidth onClick={() => setCertificateDialog(false)}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontWeight: 700,
              borderColor: C.border, color: C.textSecondary,
              border: `1px solid ${C.border}`,
              "&:hover": { borderColor: C.textSecondary, bgcolor: C.borderSoft },
            }}
          >
            Later
          </Button>
          <Button
            fullWidth variant="contained" onClick={requestCertificate}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontWeight: 700,
              background: `linear-gradient(135deg, ${C.gold}, #D97706)`,
              color: "#fff", boxShadow: "none",
              "&:hover": { boxShadow: `0 4px 14px ${C.gold}45` },
            }}
          >
            Get Certificate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2.5, fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </Box>
  );
}